/**
 * Document metadata.
 *
 * The application is a single-page app with no server-side rendering, so the head
 * is managed imperatively. This covers what a crawler and a social preview actually
 * read: title, description, canonical URL and a robots directive.
 *
 * PRIVATE SURFACES ARE MARKED `noindex`. Anything behind a session — the dashboard,
 * a profile, the admin console, a participation screen — must never be indexed, and
 * neither must the results of a contest that has not been published.
 */

import { useEffect } from 'react';

const SITE_NAME = 'ITA6';

function upsertMeta(selector: string, create: () => HTMLElement, apply: (el: HTMLElement) => void): void {
  let element = document.head.querySelector(selector) as HTMLElement | null;
  if (!element) {
    element = create();
    document.head.appendChild(element);
  }
  apply(element);
}

export interface PageMeta {
  title: string;
  description?: string;
  /** Absolute or root-relative canonical path for this page. */
  canonicalPath?: string;
  /** Keep this page out of search engines. */
  noIndex?: boolean;
  /** Open Graph type; `website` unless a page says otherwise. */
  type?: 'website' | 'article';
}

export function applyPageMeta(meta: PageMeta): void {
  const fullTitle = meta.title.includes(SITE_NAME) ? meta.title : `${meta.title} · ${SITE_NAME}`;
  document.title = fullTitle;

  upsertMeta(
    'meta[name="description"]',
    () => {
      const element = document.createElement('meta');
      element.setAttribute('name', 'description');
      return element;
    },
    (element) => {
      if (meta.description) element.setAttribute('content', meta.description);
    },
  );

  upsertMeta(
    'meta[property="og:title"]',
    () => {
      const element = document.createElement('meta');
      element.setAttribute('property', 'og:title');
      return element;
    },
    (element) => element.setAttribute('content', fullTitle),
  );

  upsertMeta(
    'meta[property="og:type"]',
    () => {
      const element = document.createElement('meta');
      element.setAttribute('property', 'og:type');
      return element;
    },
    (element) => element.setAttribute('content', meta.type ?? 'website'),
  );

  upsertMeta(
    'meta[name="robots"]',
    () => {
      const element = document.createElement('meta');
      element.setAttribute('name', 'robots');
      return element;
    },
    (element) => {
      element.setAttribute('content', meta.noIndex ? 'noindex, nofollow' : 'index, follow');
    },
  );

  // Canonical: only meaningful for public pages, since a private page should not
  // advertise a canonical URL at all.
  const existing = document.head.querySelector('link[rel="canonical"]');
  if (meta.noIndex) {
    existing?.remove();
    return;
  }
  if (meta.canonicalPath) {
    const url = new URL(meta.canonicalPath, window.location.origin).toString();
    if (existing) existing.setAttribute('href', url);
    else {
      const link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', url);
      document.head.appendChild(link);
    }
  }
}

/** React hook form: applies the metadata while the page is mounted. */
export function usePageMeta(meta: PageMeta): void {
  useEffect(() => {
    applyPageMeta(meta);
    // Deliberately keyed on the field values rather than the object identity, so a
    // caller passing an inline literal does not re-run on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.title, meta.description, meta.canonicalPath, meta.noIndex, meta.type]);
}

/** Convenience for pages that are private by nature. */
export function usePrivatePage(title: string): void {
  usePageMeta({ title, noIndex: true });
}
